import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import apiService from '../../services/api.service'
import { createApiCase } from 'helper/create.api.case'
import { createApiThunk } from 'helper/create.api.thunk'

export const getConsignList = createApiThunk(
    'getConsignList',
    apiService.getConsignList
)

export const getPendingCount = createApiThunk(
    'getPendingCount',
    apiService.getPendingCount
)

export const createConsign = createApiThunk(
    'createConsign',
    apiService.createConsign
)

export const getConsignInfo = createApiThunk(
    'getConsignInfo',
    apiService.getConsignInfo
)

export const updateConsign = createApiThunk(
    'updateConsign',
    apiService.updateConsign
)

export const updateConsignStatus = createApiThunk(
    'updateConsignStatus',
    apiService.updateConsignStatus
)

// Ticking a Next Action to-do line. Hands back the whole case, so the list redraws
// from the answer rather than the screen guessing what it now looks like.
export const confirmConsignChecklistItem = createApiThunk(
    'confirmConsignChecklistItem',
    apiService.confirmConsignChecklistItem
)

// Skip Inspection on the Following Up tab. Hands back the whole case, exactly as
// updateConsignStatus does, so the screen redraws from the answer.
export const skipConsignInspection = createApiThunk(
    'skipConsignInspection',
    apiService.skipConsignInspection
)

export const getConsignStatusCount = createApiThunk(
    'getConsignStatusCount',
    apiService.getConsignStatusCount
)

// The workshop's repair quote. Its own call because the report is JSON and this is a
// file; answers with the whole job so the form redraws with the new attachment on it.
export const uploadRepairQuotation = createApiThunk(
    'uploadRepairQuotation',
    apiService.uploadRepairQuotation
)

export const getInspectionList = createApiThunk(
    'getInspectionList',
    apiService.getInspectionList
)

export const getInspectionInfo = createApiThunk(
    'getInspectionInfo',
    apiService.getInspectionInfo
)

export const getInspectionTabCount = createApiThunk(
    'getInspectionTabCount',
    apiService.getInspectionTabCount
)

export const updateInspection = createApiThunk(
    'updateInspection',
    apiService.updateInspection
)

/** Start Inspection / Complete - the two buttons on the inspection screen. */
export const updateInspectionStatus = createApiThunk(
    'updateInspectionStatus',
    apiService.updateInspectionStatus
)

export const createInspectionOrder = createApiThunk(
    'createInspectionOrder',
    apiService.createInspectionOrder
)

export const publishProduct = createApiThunk(
    'publishProduct',
    apiService.publishProduct
)

export const initialData = {
    serviceSort: {
        active_page: 1,
        start: 0,
        length: 10,
        search: '',
        follow_up_loading: false,
        type: 'new',
        // Empty means the backend's own order: most recently worked first.
        sort: '',
        order: '',
    },
    consignData: {
        list: [],
        total: 0,
        loading: false,
    },
    inspectData: {
        list: [],
        total: 0,
        loading: false,
    },
    inspectInfo: {},
    inspectInfoLoading: false,
    consignInfo: {},
    consignInfoLoading: false,
    // A SAVE in flight, as distinct from the record being fetched: the form stays
    // mounted under a cover while this is true, instead of collapsing to a skeleton
    // and closing every section the operation user had open.
    savingConsign: false,
    // One number per tab, straight off the endpoint that also drives the listing, so a
    // badge cannot disagree with the rows behind the tab it labels.
    consignTabCounts: {},
    inspectTabCounts: {},
    pendingCount: {},
}

const dataSlice = createSlice({
    name: 'service/data',
    initialState: initialData,
    reducers: {
        setServiceSort: (state, action) => {
            state.serviceSort = action.payload
        },
        clearServiceSort: (state, action) => {
            state.serviceSort = {
                active_page: 1,
                start: 0,
                length: 10,
                search: '',
                follow_up_loading: false,
                type: 'new',
                sort: '',
                order: '',
            }
        },
    },
    extraReducers: (builder) => {
        createApiCase(builder, getPendingCount, {
            fulfilled: (state, action) => {
                state.pendingCount = action.payload
            },
        })
        builder
            .addCase(getConsignList.pending, (state, action) => {
                state.consignRequestId = action.meta.requestId
                state.consignData.loading = true
                state.serviceSort.follow_up_loading = false
            })
            .addCase(getConsignList.fulfilled, (state, action) => {
                if (state.consignRequestId !== action.meta.requestId) return
                state.consignData.loading = false
                state.serviceSort.follow_up_loading = false

                state.consignData.list = action.payload.data.aaData
                state.consignData.total =
                    action.payload.data.iTotalDisplayRecords
            })
            .addCase(getConsignList.rejected, (state, action) => {
                if (state.consignRequestId !== action.meta.requestId) return
                state.consignData.loading = false
                state.serviceSort.follow_up_loading = false
            })
            .addCase(createConsign.pending, (state) => {
                state.consignInfoLoading = true
            })
            .addCase(createConsign.fulfilled, (state, action) => {
                state.consignInfoLoading = false
                state.consignInfo = action.payload.data
            })
            .addCase(createConsign.rejected, (state) => {
                state.consignInfoLoading = false
            })
            .addCase(getConsignInfo.pending, (state) => {
                state.consignInfoLoading = true
            })
            .addCase(getConsignInfo.fulfilled, (state, action) => {
                state.consignInfoLoading = false
                state.consignInfo = action.payload.data
            })
            .addCase(getConsignInfo.rejected, (state) => {
                state.consignInfoLoading = false
            })
            // A save and a stage change both answer with the whole record in the same
            // shape as the detail endpoint, so the form re-renders off the response
            // instead of firing a second GET to find out what it just did.
            .addCase(updateConsignStatus.pending, (state) => {
                state.consignInfoLoading = true
                state.serviceSort.follow_up_loading = true
            })
            .addCase(updateConsignStatus.fulfilled, (state, action) => {
                state.consignInfoLoading = false
                state.serviceSort.follow_up_loading = false
                state.consignInfo = action.payload.data
            })
            .addCase(updateConsignStatus.rejected, (state) => {
                state.consignInfoLoading = false
                state.serviceSort.follow_up_loading = false
            })
            .addCase(getConsignStatusCount.fulfilled, (state, action) => {
                state.consignTabCounts = action.payload.data?.tab_counts || {}
            })
            .addCase(updateConsign.pending, (state) => {
                state.consignData.loading = true
                state.serviceSort.follow_up_loading = true
                state.savingConsign = true
            })
            .addCase(updateConsign.fulfilled, (state, action) => {
                state.consignData.loading = false
                state.serviceSort.follow_up_loading = false
                state.savingConsign = false
                if (action.payload?.data) {
                    state.consignInfo = action.payload.data
                }
            })
            .addCase(updateConsign.rejected, (state) => {
                state.consignData.loading = false
                state.serviceSort.follow_up_loading = false
                state.savingConsign = false
            })
            .addCase(getInspectionInfo.pending, (state) => {
                state.inspectInfoLoading = true
            })
            .addCase(getInspectionInfo.fulfilled, (state, action) => {
                state.inspectInfoLoading = false
                state.inspectInfo = action.payload.data
            })
            .addCase(getInspectionInfo.rejected, (state) => {
                state.inspectInfoLoading = false
            })
            .addCase(getInspectionTabCount.fulfilled, (state, action) => {
                state.inspectTabCounts = action.payload.data?.tab_counts || {}
            })
            .addCase(getInspectionList.pending, (state) => {
                state.inspectData.loading = true
            })
            .addCase(getInspectionList.fulfilled, (state, action) => {
                state.inspectData.loading = false
                state.inspectData.list = action.payload.data.aaData
                state.inspectData.total =
                    action.payload.data.iTotalDisplayRecords
            })
            .addCase(getInspectionList.rejected, (state) => {
                state.inspectData.loading = false
            })
            .addCase(updateInspection.pending, (state) => {
                state.inspectData.loading = true
            })
            .addCase(updateInspection.fulfilled, (state, action) => {
                state.inspectData.loading = false
                // The save answers with the job as it now stands - activity log,
                // start time, grade included. Discarding it left the screen showing
                // a log one save behind until a full refresh.
                if (action.payload?.data) {
                    state.inspectInfo = action.payload.data
                }
            })
            .addCase(updateInspection.rejected, (state) => {
                state.inspectData.loading = false
            })
            // The response is the job as it now stands, so the screen re-seeds from it
            // rather than refetching - the button that was pressed is the same request
            // that answers what the next one should say.
            .addCase(updateInspectionStatus.pending, (state) => {
                state.inspectInfoLoading = true
            })
            .addCase(updateInspectionStatus.fulfilled, (state, action) => {
                state.inspectInfoLoading = false
                state.inspectInfo = action.payload.data
            })
            .addCase(updateInspectionStatus.rejected, (state) => {
                state.inspectInfoLoading = false
            })
            .addCase(confirmConsignChecklistItem.fulfilled, (state, action) => {
                state.consignInfo = action.payload.data
            })
            .addCase(skipConsignInspection.pending, (state) => {
                state.consignInfoLoading = true
                state.serviceSort.follow_up_loading = true
            })
            .addCase(skipConsignInspection.fulfilled, (state, action) => {
                state.consignInfoLoading = false
                state.serviceSort.follow_up_loading = false
                state.consignInfo = action.payload.data
            })
            .addCase(skipConsignInspection.rejected, (state) => {
                state.consignInfoLoading = false
                state.serviceSort.follow_up_loading = false
            })
            .addCase(createInspectionOrder.pending, (state) => {
                state.consignData.loading = true
                state.serviceSort.follow_up_loading = true
            })
            .addCase(createInspectionOrder.fulfilled, (state, action) => {
                state.consignData.loading = false
                state.serviceSort.follow_up_loading = false
            })
            .addCase(createInspectionOrder.rejected, (state) => {
                state.consignData.loading = false
                state.serviceSort.follow_up_loading = false
            })
            .addCase(publishProduct.pending, (state) => {
                state.consignData.loading = true
                state.serviceSort.follow_up_loading = true
            })
            .addCase(publishProduct.fulfilled, (state, action) => {
                state.consignData.loading = false
                state.serviceSort.follow_up_loading = false
            })
            .addCase(publishProduct.rejected, (state) => {
                state.consignData.loading = false
                state.serviceSort.follow_up_loading = false
            })
    },
})

export const { setServiceSort, clearServiceSort } = dataSlice.actions

export default dataSlice.reducer
